import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { ShieldCheck, ChevronDown, ChevronUp } from 'lucide-react';
import { getMe } from '@/lib/bootRequests';

const LS_KEY = 'dividrip:adminButtonsOpen';

/**
 * AdminButtonsCollapse — Round 86 (Feb 2026).
 *
 * Wraps the Dashboard admin-button row so the buttons are HIDDEN by
 * default. The wrapper:
 *   • Reads the shared `useAuth()` context FIRST (synchronous — if
 *     AuthContext already knows the caller isn't admin, we short-
 *     circuit before ever hitting the DOM). Round P111u belt-and-
 *     suspenders after a prod-report showed admin buttons rendering
 *     for a logged-out visitor whose deploy was stale.
 *   • Then calls `/api/auth/me` as a secondary check — if the API
 *     also confirms admin, only then do the children mount.
 *   • If admin, shows a small "Show admin buttons" toggle. Clicking
 *     mounts and reveals the children. State persists across reloads
 *     in `localStorage` so the admin's preferred default sticks.
 *
 * Defense-in-depth: each admin button ALSO has its OWN useAuth guard
 * (Round P111u), so even if a non-admin somehow forces our state open,
 * the buttons individually render nothing.
 */
const loadOpen = () => {
  try {
    return localStorage.getItem(LS_KEY) === '1';
  } catch { return false; }
};

export default function AdminButtonsCollapse({ children }) {
  const { user, loading } = useAuth();
  // Synchronous first gate — AuthContext resolves before the DOM paints
  // in the common case. If the user object is already known to be
  // non-admin (or loading, or not-logged-in), we return null immediately.
  const contextSaysAdmin = !!(user && typeof user === 'object' && user.role === 'admin');
  const [apiAdmin, setApiAdmin] = useState(false);
  const [open, setOpen] = useState(loadOpen);

  useEffect(() => {
    // Only fire the secondary API check if the context already thinks
    // this is an admin — no need to hit /auth/me for anonymous visitors.
    if (!contextSaysAdmin) { setApiAdmin(false); return; }
    let cancelled = false;
    getMe()
      .then((d) => { if (!cancelled && d?.role === 'admin') setApiAdmin(true); })
      .catch(() => { if (!cancelled) setApiAdmin(false); });
    return () => { cancelled = true; };
  }, [contextSaysAdmin]);

  useEffect(() => {
    try { localStorage.setItem(LS_KEY, open ? '1' : '0'); } catch { /* swallow */ }
  }, [open]);

  // Belt-and-suspenders: BOTH the context AND the API must confirm admin.
  // A stale deploy that only fixes one layer still can't leak buttons.
  if (loading) return null;
  if (!contextSaysAdmin) return null;
  if (!apiAdmin) return null;

  return (
    <div className="mt-1" data-testid="admin-buttons-collapse">
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-violet-700 dark:text-violet-300 hover:text-violet-900 dark:hover:text-violet-100 transition-colors px-2 py-1 rounded hover:bg-violet-50/60 dark:hover:bg-violet-950/30"
          data-testid="admin-buttons-toggle"
          title={open ? 'Hide admin buttons' : 'Show admin buttons'}
        >
          <ShieldCheck className="w-3.5 h-3.5" />
          {open ? 'Hide admin buttons' : 'Show admin buttons'}
          {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>
      </div>
      {open && (
        <div
          className="flex flex-wrap items-center justify-end gap-2 mt-1"
          data-testid="admin-buttons-row"
        >
          {children}
        </div>
      )}
    </div>
  );
}
